import { useTranslations } from "use-intl";

import { FormatFilterControl } from "@/components/format-filter-control";
import type { LinkTarget } from "@/components/link";
import type { DealOfferFormatOption } from "@/components/marketplace/deal-offers-types";
import { DealOfferFormatTab } from "@/components/marketplace/DealOfferFormatTab";
import { Price } from "@/components/Price";

export function DealOfferFormatTabs({
  activeFormat,
  formatLinkTarget,
  options,
  onSelect,
  showSingleOption = false,
  variant = "cards",
}: {
  activeFormat: string | null;
  formatLinkTarget?: (format: DealOfferFormatOption["format"]) => LinkTarget;
  options: DealOfferFormatOption[];
  onSelect: (format: DealOfferFormatOption["format"]) => void;
  /** Segmented only: keep a lone format visible so the panel always names the offer format. */
  showSingleOption?: boolean;
  /** `cards` is the explorer grid; `segmented` is the compact switch inside the hero offer panel. */
  variant?: "cards" | "segmented";
}) {
  const t = useTranslations("shared.affiliates");

  if (options.length === 0) return null;
  if (options.length === 1 && !(showSingleOption && variant === "segmented")) return null;

  if (variant === "segmented") {
    return (
      <FormatFilterControl
        all={false}
        aria-label={t("offerFormatSwitchLabel")}
        value={activeFormat ?? ""}
        onChange={(format) => onSelect(format as DealOfferFormatOption["format"])}
        linkTarget={
          formatLinkTarget
            ? (format) => formatLinkTarget(format as DealOfferFormatOption["format"])
            : undefined
        }
        options={options.map((option) => ({
          value: option.format,
          count: option.offerCount,
          badge:
            option.availability === "out_of_stock" ? (
              t("outOfStock")
            ) : option.price && option.currency ? (
              <Price amount={option.price} currency={option.currency} compact plain />
            ) : undefined,
        }))}
      />
    );
  }

  return (
    <div
      className="grid w-full gap-2 [grid-template-columns:repeat(auto-fit,minmax(min(100%,17rem),1fr))]"
      role="group"
      aria-label={t("filterByFormat")}
    >
      {options.map((option) => (
        <DealOfferFormatTab
          key={option.format}
          active={option.format === activeFormat}
          formatLinkTarget={formatLinkTarget}
          onSelect={onSelect}
          option={option}
        />
      ))}
    </div>
  );
}
